import { FC } from 'react';
import { VStack } from '@chakra-ui/react';

import {
  ScheduledBankTransferEndDateDeterminedBy,
  ScheduledBankTransferFrequency,
} from '__generated__/GQL';
import SunshineCard from 'components/_core/SunshineCard';
import SquircleIcon from 'components/_core/SunshineSquircleIcon';
import Text from 'components/_core/Text';
import {
  BankTransferInputSchedule,
  useBankTransferContext,
} from 'features/Payments/Pages/Transfers/contexts';
import { formatDate } from 'helpers/date';

import { frequencyLocales } from '../../../commonLocales';
import { computeLastDueDate } from '../../../utils';
import locales from './locales';

interface ScheduledTransferCardActionProps {
  onClick: () => void;
  isSideBarOpen: boolean;
}

const composeSubtitle = ({
  endDateDeterminedBy,
  frequency,
  lastDueDate,
  nextDueDate,
  remainingNumberOfOccurrences,
}: BankTransferInputSchedule) => {
  if (frequency === ScheduledBankTransferFrequency.Once) {
    return locales.formatString(locales.onDate, {
      date: formatDate(new Date(nextDueDate), 'eeee_PP'),
    });
  }

  if (
    endDateDeterminedBy ===
      ScheduledBankTransferEndDateDeterminedBy.NumberOfOccurrences &&
    !!remainingNumberOfOccurrences
  ) {
    return locales.formatString(locales.fromDateToDate, {
      end: formatDate(
        computeLastDueDate({
          frequency,
          nextDueDate,
          numberOfOccurrences: remainingNumberOfOccurrences,
        }),
        'PP',
      ),
      start: formatDate(new Date(nextDueDate), 'eeee_PP'),
    });
  }

  if (
    endDateDeterminedBy ===
      ScheduledBankTransferEndDateDeterminedBy.ExplicitDate &&
    !!lastDueDate
  ) {
    return locales.formatString(locales.fromDateToDate, {
      end: formatDate(new Date(lastDueDate), 'PP'),
      start: formatDate(new Date(nextDueDate), 'eeee_PP'),
    });
  }

  if (!endDateDeterminedBy) {
    return locales.formatString(locales.fromDate, {
      date: formatDate(new Date(nextDueDate), 'eeee_PP'),
    });
  }

  return locales.scheduleDescription;
};

const ScheduledTransferCardAction: FC<ScheduledTransferCardActionProps> = ({
  isSideBarOpen,
  onClick,
}) => {
  const {
    bankTransfer: { schedule },
  } = useBankTransferContext('setExternalDetails');

  const { subtitle, title } = schedule
    ? {
        subtitle: composeSubtitle(schedule),
        title: frequencyLocales[schedule.frequency],
      }
    : { subtitle: locales.scheduleDescription, title: locales.schedule };

  return (
    <VStack alignItems="start" spacing="space-4">
      <Text size="sm">{locales.settings}</Text>
      <SunshineCard.Group>
        <SunshineCard isActive={isSideBarOpen} onClick={onClick} title={title}>
          <SunshineCard.Slot name="aside">
            <SquircleIcon borderRadius="radius-12" name="calendar" />
          </SunshineCard.Slot>
          <SunshineCard.Text>{subtitle}</SunshineCard.Text>
          <SunshineCard.Slot name="action">
            <SunshineCard.SunshineIcon name="chevron-right" />
          </SunshineCard.Slot>
        </SunshineCard>
      </SunshineCard.Group>
    </VStack>
  );
};

export default ScheduledTransferCardAction;
